import { useCallback, useEffect, useMemo, useState } from "react"

import { getModels } from "@/lib/api"
import type { Model } from "@/types/api"

const MODEL_KEY = "celver-copilot:model"
const EFFORT_KEY = "celver-copilot:effort"
const DEFAULT_EFFORT = "medium"

/**
 * Model catalog plus the user's pick and reasoning effort, persisted per browser.
 *
 * `selectedId` stays null until the catalog confirms the stored model still
 * exists — a model retired on the gateway must not be sent back to the server.
 */
export function useModels() {
  const [models, setModels] = useState<Model[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  // The user's standing preference — kept even while a model that lacks it is
  // selected, so switching back to e.g. an Anthropic model restores "max".
  const [preferredEffort, setPreferredEffort] = useState<string | null>(() => localStorage.getItem(EFFORT_KEY))

  useEffect(() => {
    let cancelled = false
    getModels()
      .then(({ models: available, default: serverDefault }) => {
        if (cancelled) return
        setModels(available)
        const stored = localStorage.getItem(MODEL_KEY)
        const known = available.some((m) => m.id === stored)
        if (stored && !known) localStorage.removeItem(MODEL_KEY)
        setSelectedId(known ? stored : serverDefault)
      })
      .catch(() => {
        // The picker is an enhancement — chat falls back to the server default
      })
    return () => {
      cancelled = true
    }
  }, [])

  const selectModel = useCallback((id: string) => {
    localStorage.setItem(MODEL_KEY, id)
    setSelectedId(id)
  }, [])

  const selectEffort = useCallback((level: string) => {
    localStorage.setItem(EFFORT_KEY, level)
    setPreferredEffort(level)
  }, [])

  const effortLevels = useMemo(
    () => models.find((m) => m.id === selectedId)?.effort_levels ?? [],
    [models, selectedId],
  )

  /** What the slider shows *and* what goes on the wire — the two must not diverge. */
  const effort = useMemo(() => {
    if (!effortLevels.length) return null
    if (preferredEffort && effortLevels.includes(preferredEffort)) return preferredEffort
    if (effortLevels.includes(DEFAULT_EFFORT)) return DEFAULT_EFFORT
    return effortLevels[Math.floor((effortLevels.length - 1) / 2)]
  }, [effortLevels, preferredEffort])

  return { models, selectedId, selectModel, effortLevels, effort, selectEffort }
}
